import type { LookupEntry } from '@/types/bundle'

// The backend schema (lookup_table.schema.json) never constrained `type`
// beyond `{type: "string"}` - the fixed two-value set this used to hardcode
// was this app's own assumption, not a real backend limit. Kept only as
// display polish for the two values every real bundle has used so far;
// anything else is shown as typed, snake_case pretty-printed if it looks
// like it (matching document-types/labels.ts's own snakeToTitleCase - not
// cross-imported from that feature folder into this shared lib, kept as its
// own small copy here instead).
const LEGACY_LOOKUP_TYPE_LABELS: Record<string, string> = {
  estate_agent: 'Estate Agent',
  solicitor_firm: 'Solicitor Firm',
}

const SNAKE_CASE_RE = /^[a-z0-9]+(_[a-z0-9]+)*$/

function titleCaseFromSnakeCase(value: string): string {
  return value
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

export function lookupTypeLabel(type: string): string {
  if (LEGACY_LOOKUP_TYPE_LABELS[type]) return LEGACY_LOOKUP_TYPE_LABELS[type]
  return SNAKE_CASE_RE.test(type) ? titleCaseFromSnakeCase(type) : type
}

export interface LookupTypeOption {
  value: string
  label: string
}

// Every distinct type currently in use across the table's own entries, plus
// the two legacy defaults as a baseline (so a freshly-emptied table, or one
// that happens to only use one of the two today, still offers a sensible
// starting pair) - never a fixed, closed list. Shared between the Lookup
// Table screen (the entry's own `type`) and document-types'
// LookupResolutionSection (`FieldLookup.entry_type`, which must reference
// one of these same real values to match anything at request time).
export function deriveLookupTypeOptions(entries: LookupEntry[]): LookupTypeOption[] {
  const seen = new Set<string>(['estate_agent', 'solicitor_firm'])
  entries.forEach((entry) => {
    if (entry.type.trim().length > 0) seen.add(entry.type)
  })
  return Array.from(seen)
    .sort((a, b) => lookupTypeLabel(a).localeCompare(lookupTypeLabel(b)))
    .map((type) => ({ value: type, label: lookupTypeLabel(type) }))
}
